import React, { useState } from "react";
import { Train } from "lucide-react";

/**
 * SafeImage — `<img>` wrapper with a styled fallback when the source fails.
 *
 * If the network image 404s or hangs, we render a deterministic gold-on-ink
 * placeholder showing the alt text initials. Used in city cards, hero
 * photos, accommodation thumbnails — anywhere a third-party CDN might glitch.
 *
 * Props (forwarded to img): src, alt, className, style.
 */
function initials(s = "") {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 3);
}

export default function SafeImage({ src, alt = "", className = "", style, ...rest }) {
  const [failed, setFailed] = useState(!src);

  if (failed || !src) {
    return (
      <div
        role="img"
        aria-label={alt}
        data-testid="safe-image-fallback"
        className={`flex flex-col items-center justify-center bg-gradient-to-br from-panel via-ink to-navy text-gold/70 ${className}`}
        style={style}
      >
        <Train className="h-1/4 w-1/4 max-h-12 max-w-12 mb-2 opacity-50" strokeWidth={1.2} />
        <span className="font-display tracking-[0.25em] text-base md:text-xl">
          {initials(alt) || "—"}
        </span>
        <span className="text-[10px] text-cream-dim/60 mt-1 px-3 text-center font-serif italic line-clamp-1">
          {alt}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => setFailed(true)}
      loading="lazy"
      {...rest}
    />
  );
}
